import { ArrowLeft, Settings as SettingsIcon, Upload, X } from "lucide-react";
import React, { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useGenerationStatus, useTilesetGeneration } from "../../hooks/useWFC";
import type { TilesetGenerationParams } from "../../types/wfc";

// Do we need to use any of these ?? We did in an old implementation of this page
// import { useQuery, useMutation } from '@tanstack/react-query';
// import { tilesetService } from '../services/tilesetService';
// import { useDefaultTilesets } from '../../hooks/usePresets';
// import { Settings } from 'lucide-react';
// import { PageLayout } from '../components/layout/PageLayout';
// import { Card } from '../components/ui/Card';
// import { LoadingOverlay } from '../components/LoadingOverlay';
// import { ErrorMessage } from '../components/ui/ErrorMessage';
// import { AnimatedGenerationPreview } from '../components/generation/AnimatedGenerationPreview';
// import { GenerationSettings } from '../components/generation/GenerationSettings';
// import { TilesetGrid } from '../components/generation/TilesetGrid';

interface TilesetProperties extends TilesetGenerationParams {
  width: number;
  height: number;
  tileSize: number;
  symmetry: boolean;
  allowRotations: boolean;
}

export default function TilesetPage() {
  const [showProperties, setShowProperties] = useState(true);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [properties, setProperties] = useState<TilesetProperties>({
    width: 32,
    height: 32,
    tileSize: 16,
    symmetry: false,
    allowRotations: true,
  });

  const generation = useTilesetGeneration();
  const status = useGenerationStatus(generation.data?.id ?? null);

  const handleFileDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file?.type.startsWith("image/")) {
      setSelectedFile(file);
    }
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setSelectedFile(file);
      }
    },
    [],
  );

  const handleGenerate = useCallback(async () => {
    if (!selectedFile) return;

    try {
      await generation.mutateAsync({
        file: selectedFile,
        params: properties,
      });
    } catch (error) {
      console.error("Generation failed:", error);
    }
  }, [selectedFile, properties, generation]);

  return (
    <div className="h-screen flex">
      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/" className="btn btn-ghost btn-sm">
              <ArrowLeft size={20} />
              <span className="ml-2">Back to Home</span>
            </Link>
            <h1 className="text-2xl font-bold">Tiling Generator</h1>
          </div>

          <div className="flex items-center gap-2">
            <button
              className="btn btn-ghost btn-circle"
              onClick={() => setShowProperties(!showProperties)}
              aria-label="Toggle properties panel"
            >
              <SettingsIcon size={20} />
            </button>
          </div>
        </div>

        {/* Upload Area */}
        <div className="text-center mb-6">
          <h3 className="font-medium mb-2">Upload Tileset</h3>
        </div>

        {/* Upload/Preview Area */}
        {selectedFile ? (
          <div className="border-2 border-base-content/20 rounded-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-medium">{selectedFile.name}</h3>
                <p className="text-sm text-base-content/60">
                  {Math.round(selectedFile.size / 1024)}KB
                </p>
              </div>
              <button
                className="btn btn-ghost btn-sm btn-circle"
                onClick={() => setSelectedFile(null)}
                aria-label="Remove selected file"
              >
                <X size={20} />
              </button>
            </div>

            <img
              src={URL.createObjectURL(selectedFile)}
              alt="Selected tileset"
              className="max-w-xl mx-auto rounded-lg"
            />

            {status.data?.status === "completed" && status.data.outputUrl && (
              <div className="mt-6">
                <h3 className="font-medium mb-2">Generated Tilemap</h3>
                <img
                  src={status.data.outputUrl}
                  alt="Generated tilemap"
                  className="max-w-xl mx-auto rounded-lg"
                />
              </div>
            )}

            {status.data?.status === "failed" && (
              <div className="alert alert-error mt-4">
                {status.data.error || "Generation failed"}
              </div>
            )}
          </div>
        ) : (
          <div
            className="border-2 border-dashed border-base-content/20 rounded-lg p-12 text-center"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleFileDrop}
          >
            <div className="max-w-xl mx-auto">
              <Upload className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium mb-2">Upload Your Tileset</h3>
              <p className="text-base-content/60 mb-4">
                Drag and drop your tileset image and XML ruleset here, or click
                to select files
              </p>
              <div className="flex flex-col gap-4 items-center">
                <div className="w-full max-w-xs">
                  <label htmlFor="tileset-image" className="sr-only">
                    Upload tileset image
                  </label>
                  <input
                    id="tileset-image"
                    type="file"
                    className="file-input file-input-bordered file-input-primary w-full max-w-xs"
                    accept="image/*"
                    onChange={handleFileSelect}
                    aria-label="Upload tileset image"
                  />
                </div>
                <div className="w-full max-w-xs">
                  <label htmlFor="tileset-xml" className="sr-only">
                    Upload XML ruleset
                  </label>
                  <input
                    id="tileset-xml"
                    type="file"
                    className="file-input file-input-bordered file-input-primary w-full max-w-xs"
                    accept=".xml"
                    onChange={handleFileSelect}
                    aria-label="Upload XML ruleset"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Properties Panel */}
      {showProperties && (
        <div className="w-80 border-l border-base-content/10 bg-base-200 p-4 overflow-y-auto">
          <div className="sticky top-0">
            <h2 className="font-bold mb-4">Properties</h2>

            <div className="space-y-4">
              <div className="form-control">
                <label className="label" htmlFor="output-width">
                  <span className="label-text">Output Width</span>
                </label>
                <input
                  id="output-width"
                  type="number"
                  value={properties.width}
                  onChange={(e) =>
                    setProperties({
                      ...properties,
                      width: parseInt(e.target.value),
                    })
                  }
                  className="input input-bordered"
                  min={1}
                  max={256}
                />
              </div>

              <div className="form-control">
                <label className="label" htmlFor="output-height">
                  <span className="label-text">Output Height</span>
                </label>
                <input
                  id="output-height"
                  type="number"
                  value={properties.height}
                  onChange={(e) =>
                    setProperties({
                      ...properties,
                      height: parseInt(e.target.value),
                    })
                  }
                  className="input input-bordered"
                  min={1}
                  max={256}
                />
              </div>

              <div className="form-control">
                <label className="label" htmlFor="tile-size">
                  <span className="label-text">Tile Size</span>
                </label>
                <input
                  id="tile-size"
                  type="number"
                  value={properties.tileSize}
                  onChange={(e) =>
                    setProperties({
                      ...properties,
                      tileSize: parseInt(e.target.value),
                    })
                  }
                  className="input input-bordered"
                  min={8}
                  max={64}
                  step={8}
                />
              </div>

              <div className="form-control">
                <label
                  className="label cursor-pointer"
                  htmlFor="enable-symmetry"
                >
                  <span className="label-text">Enable Symmetry</span>
                  <input
                    id="enable-symmetry"
                    type="checkbox"
                    checked={properties.symmetry}
                    onChange={(e) =>
                      setProperties({
                        ...properties,
                        symmetry: e.target.checked,
                      })
                    }
                    className="checkbox"
                  />
                </label>
              </div>

              <div className="form-control">
                <label
                  className="label cursor-pointer"
                  htmlFor="allow-rotations"
                >
                  <span className="label-text">Allow Rotations</span>
                  <input
                    id="allow-rotations"
                    type="checkbox"
                    checked={properties.allowRotations}
                    onChange={(e) =>
                      setProperties({
                        ...properties,
                        allowRotations: e.target.checked,
                      })
                    }
                    className="checkbox"
                  />
                </label>
              </div>

              <button
                className={`btn btn-primary w-full ${
                  status.isLoading ? "loading" : ""
                }`}
                onClick={handleGenerate}
                disabled={!selectedFile || status.isLoading}
                aria-label="Generate tilemap"
              >
                {status.isLoading ? "Generating..." : "Generate Tilemap"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
